/**
 * Pass Service Layer
 * Handles approval/rejection of inductions and pass management
 */

import { supabase } from '../supabaseClient';
import { getKioskToken } from '@shared/services/kioskAuthService';

/**
 * Approve induction and send pass email (combined operation)
 * Email failure is non-fatal - approval succeeds even if email fails
 */
export async function approveAndEmailPass(publicReference, notes = null) {
  try {
    return await approveAndEmailPassBff(publicReference, notes);
  } catch (error) {
    console.error('Error in approve and email workflow:', error);
    throw error;
  }
}

/**
 * Approve induction via Edge Function BFF
 */
export async function approveAndEmailPassBff(publicReference, notes = null) {
  try {
    if (!publicReference) {
      throw new Error('Reference code is required');
    }

    const { data, error } = await supabase.functions.invoke('admin-approve-induction', {
      body: {
        public_reference: publicReference,
        approve: true,
        notes,
      },
    });

    if (error) throw error;
    if (!data?.ok) {
      throw new Error(data?.error?.message || 'Approval failed');
    }

    return {
      success: true,
      approval: data,
      email: data?.email || null,
      emailError: data?.email_error || null,
    };
  } catch (error) {
    console.error('Error approving induction via BFF:', error);
    throw error;
  }
}

/**
 * Kiosk scan via Edge Function BFF
 */
export async function kioskScanBff(passToken, { action = 'check_in', siteId = null, kioskId = null } = {}) {
  try {
    if (!passToken) {
      throw new Error('Pass token is required');
    }

    const kioskToken = getKioskToken();
    if (!kioskToken) {
      throw new Error('Kiosk is not authenticated');
    }

    const { data, error } = await supabase.functions.invoke('kiosk-scan', {
      body: {
        pass_token: String(passToken).trim(),
        site_id: siteId,
        kiosk_id: kioskId,
        action,
      },
      headers: {
        'x-kiosk-token': kioskToken,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error calling kiosk scan BFF:', error);
    throw error;
  }
}

/**
 * Validate pass at kiosk (for kiosk component)
 */
export async function validatePassAtKiosk(passToken) {
  try {
    if (!passToken) {
      throw new Error('Pass token is required');
    }

    const normalizedToken = String(passToken).trim();

    const { data, error } = await supabase.rpc('kiosk_validate_pass', {
      p_pass_token: normalizedToken,
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error validating pass:', error);
    throw error;
  }
}

/**
 * Check in with pass at kiosk
 */
export async function kioskCheckin(passToken) {
  try {
    if (!passToken) {
      throw new Error('Pass token is required');
    }

    const { data, error } = await supabase.rpc('kiosk_checkin', {
      p_pass_token: passToken,
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error checking in:', error);
    throw error;
  }
}
